"use strict";

type CookieValue = string | number;

// Tiny cookie helper exposed on `window` for devtools use, e.g.
//   setCookie("FORCE_ENTER", 1)            // persists ~10 years (default)
//   setCookie("FORCE_ENTER", 1, 30)        // persists for 30 days
//   setCookie("FORCE_ENTER", "", 0)        // delete
window.setCookie = function setCookie(
	name: string,
	value: CookieValue,
	days = 3650,
): string {
	const d = new Date();
	d.setTime(d.getTime() + days * 86400000);
	const s =
		encodeURIComponent(name) +
		"=" +
		encodeURIComponent(value) +
		"; path=/; SameSite=Lax; expires=" +
		d.toUTCString();
	document.cookie = s;
	return s;
};

function readCookie(name: string): string | null {
	const encoded = encodeURIComponent(name) + "=";
	const parts = String(document.cookie || "").split(";");
	for (let i = 0; i < parts.length; i++) {
		const part = parts[i].trim();
		if (!part.startsWith(encoded)) continue;
		return decodeURIComponent(part.slice(encoded.length));
	}
	return null;
}

function deleteCookie(name: string, domain?: string): void {
	const secure = location.protocol === "https:" ? "; Secure" : "";
	const cookieDomain = domain ? "; domain=" + domain : "";
	document.cookie =
		encodeURIComponent(name) +
		"=; path=/" +
		cookieDomain +
		"; SameSite=Lax" +
		secure +
		"; expires=Thu, 01 Jan 1970 00:00:00 GMT";
}

// Cross-region auth bridge: localStorage is origin-scoped, so switching from
// one regional host to another loses krunker_* auth keys. Mirror them into a
// shared cookie on *.krunker.zip and restore on boot.
function installCrossRegionAuthBridge(): void {
	const host = String(location.hostname || "").toLowerCase();
	const sharedDomain = host.endsWith(".krunker.zip") ? ".krunker.zip" : null;
	if (!sharedDomain) return;

	const AUTH_KEYS = [
		"krunker_username",
		"krunker_token",
		"krunker_id",
	];

	// Bootstrap localStorage from shared cookies before the game reads auth.
	for (const key of AUTH_KEYS) {
		const value = readCookie(key);
		if (value == null || value === "") continue;
		try {
			localStorage.setItem(key, value);
		} catch {}
	}

	const storageProto =
		typeof Storage !== "undefined" && Storage && Storage.prototype
			? Storage.prototype
			: null;
	if (!storageProto) return;

	const origSetItem = storageProto.setItem;
	const origRemoveItem = storageProto.removeItem;

	storageProto.setItem = function (key: string, value: string): void {
		const out = origSetItem.apply(this, arguments);
		if (this === localStorage && AUTH_KEYS.includes(key)) {
			if (value == null || value === "") deleteCookie(key, sharedDomain);
			else window.setCookie(key, String(value));
		}
		return out;
	};

	storageProto.removeItem = function (key: string): void {
		const out = origRemoveItem.apply(this, arguments);
		if (this === localStorage && AUTH_KEYS.includes(key)) {
			deleteCookie(key, sharedDomain);
		}
		return out;
	};
}

installCrossRegionAuthBridge();
